/**
 * A QR code encoder, so the two-factor secret never leaves the browser to be
 * drawn by someone else's service. Byte mode, error correction level M,
 * versions 1 to 40, the eight masks scored by the standard penalty rules
 * (ISO/IEC 18004). Returns the module grid; the caller draws it.
 */

type Grid = boolean[][];

const ECC_PER_BLOCK_M = [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28];
const BLOCKS_M = [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49];
/** Format bits for level M are 00. */
const ECL_BITS_M = 0;

function rawModules(ver: number): number {
  let n = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const align = Math.floor(ver / 7) + 2;
    n -= (25 * align - 10) * align - 55;
    if (ver >= 7) n -= 36;
  }
  return n;
}

function dataCodewords(ver: number): number {
  return Math.floor(rawModules(ver) / 8) - ECC_PER_BLOCK_M[ver]! * BLOCKS_M[ver]!;
}

// ---------------------------------------------------------------- Reed-Solomon over GF(256), polynomial 0x11D

function gfMul(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < degree; j += 1) {
      result[j] = gfMul(result[j]!, root);
      if (j + 1 < degree) result[j] ^= result[j + 1]!;
    }
    root = gfMul(root, 0x02);
  }
  return result;
}

function rsRemainder(data: number[], divisor: number[]): number[] {
  const result = divisor.map(() => 0);
  for (const b of data) {
    const factor = b ^ result.shift()!;
    result.push(0);
    divisor.forEach((coef, i) => { result[i] ^= gfMul(coef, factor); });
  }
  return result;
}

// ---------------------------------------------------------------- encoding

function utf8(text: string): number[] {
  return Array.from(new TextEncoder().encode(text));
}

function pickVersion(bytes: number): number {
  for (let ver = 1; ver <= 40; ver += 1) {
    const countBits = ver <= 9 ? 8 : 16;
    if (4 + countBits + bytes * 8 <= dataCodewords(ver) * 8) return ver;
  }
  throw new Error('Text too long for a QR code.');
}

function dataWithEcc(text: string): { ver: number; codewords: number[] } {
  const bytes = utf8(text);
  const ver = pickVersion(bytes.length);
  const bits: number[] = [];
  const put = (val: number, len: number) => { for (let i = len - 1; i >= 0; i -= 1) bits.push((val >>> i) & 1); };
  put(0b0100, 4);
  put(bytes.length, ver <= 9 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  const capacity = dataCodewords(ver) * 8;
  put(0, Math.min(4, capacity - bits.length));
  put(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) put(pad, 8);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));

  const numBlocks = BLOCKS_M[ver]!;
  const eccLen = ECC_PER_BLOCK_M[ver]!;
  const raw = Math.floor(rawModules(ver) / 8);
  const shortBlocks = numBlocks - (raw % numBlocks);
  const shortLen = Math.floor(raw / numBlocks);
  const divisor = rsDivisor(eccLen);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < numBlocks; i += 1) {
    const len = shortLen - eccLen + (i < shortBlocks ? 0 : 1);
    const dat = data.slice(k, k + len);
    k += len;
    const ecc = rsRemainder(dat, divisor);
    if (i < shortBlocks) dat.push(0);
    blocks.push(dat.concat(ecc));
  }
  const codewords: number[] = [];
  for (let i = 0; i < blocks[0]!.length; i += 1) {
    blocks.forEach((blk, j) => {
      if (i !== shortLen - eccLen || j >= shortBlocks) codewords.push(blk[i]!);
    });
  }
  return { ver, codewords };
}

// ---------------------------------------------------------------- matrix

function alignmentPositions(ver: number): number[] {
  if (ver === 1) return [];
  const num = Math.floor(ver / 7) + 2;
  const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (num * 2 - 2)) * 2;
  const out = [6];
  for (let pos = ver * 4 + 10; out.length < num; pos -= step) out.splice(1, 0, pos);
  return out;
}

class Matrix {
  readonly ver: number;
  readonly size: number;
  readonly m: Grid;
  readonly fn: Grid;

  constructor(ver: number) {
    this.ver = ver;
    this.size = ver * 4 + 17;
    this.m = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
    this.fn = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
  }

  set(x: number, y: number, dark: boolean): void {
    this.m[y]![x] = dark;
    this.fn[y]![x] = true;
  }

  drawFunctionPatterns(): void {
    const s = this.size;
    for (let i = 0; i < s; i += 1) {
      this.set(6, i, i % 2 === 0);
      this.set(i, 6, i % 2 === 0);
    }
    this.finder(3, 3);
    this.finder(s - 4, 3);
    this.finder(3, s - 4);
    const pos = alignmentPositions(this.ver);
    const n = pos.length;
    for (let i = 0; i < n; i += 1) {
      for (let j = 0; j < n; j += 1) {
        if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) continue;
        this.alignment(pos[i]!, pos[j]!);
      }
    }
    this.formatBits(0);
    this.versionBits();
  }

  finder(cx: number, cy: number): void {
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -4; dx <= 4; dx += 1) {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        const x = cx + dx;
        const y = cy + dy;
        if (x >= 0 && x < this.size && y >= 0 && y < this.size) this.set(x, y, d !== 2 && d !== 4);
      }
    }
  }

  alignment(cx: number, cy: number): void {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) this.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }

  formatBits(mask: number): void {
    const data = (ECL_BITS_M << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412;
    const bit = (i: number) => ((bits >>> i) & 1) !== 0;
    for (let i = 0; i <= 5; i += 1) this.set(8, i, bit(i));
    this.set(8, 7, bit(6));
    this.set(8, 8, bit(7));
    this.set(7, 8, bit(8));
    for (let i = 9; i < 15; i += 1) this.set(14 - i, 8, bit(i));
    const s = this.size;
    for (let i = 0; i < 8; i += 1) this.set(s - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i += 1) this.set(8, s - 15 + i, bit(i));
    this.set(8, s - 8, true);
  }

  versionBits(): void {
    if (this.ver < 7) return;
    let rem = this.ver;
    for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (this.ver << 12) | rem;
    for (let i = 0; i < 18; i += 1) {
      const dark = ((bits >>> i) & 1) !== 0;
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.set(a, b, dark);
      this.set(b, a, dark);
    }
  }

  placeCodewords(codewords: number[]): void {
    let i = 0;
    const s = this.size;
    for (let right = s - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < s; vert += 1) {
        for (let j = 0; j < 2; j += 1) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? s - 1 - vert : vert;
          if (!this.fn[y]![x] && i < codewords.length * 8) {
            this.m[y]![x] = ((codewords[i >>> 3]! >>> (7 - (i & 7))) & 1) !== 0;
            i += 1;
          }
        }
      }
    }
  }

  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) {
        if (this.fn[y]![x]) continue;
        let invert: boolean;
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break;
          case 1: invert = y % 2 === 0; break;
          case 2: invert = x % 3 === 0; break;
          case 3: invert = (x + y) % 3 === 0; break;
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
        }
        if (invert) this.m[y]![x] = !this.m[y]![x];
      }
    }
  }

  penalty(): number {
    const s = this.size;
    let score = 0;
    const lines = (get: (a: number, b: number) => boolean) => {
      for (let a = 0; a < s; a += 1) {
        let run = 1;
        for (let b = 1; b <= s; b += 1) {
          if (b < s && get(a, b) === get(a, b - 1)) { run += 1; continue; }
          if (run >= 5) score += run - 2;
          run = 1;
        }
        // Finder-like 1:1:3:1:1 with four light modules either side.
        for (let b = 0; b + 10 < s + 4; b += 1) {
          const at = (k: number) => (k >= 0 && k < s ? get(a, k) : false);
          const core = at(b) && !at(b + 1) && at(b + 2) && at(b + 3) && at(b + 4) && !at(b + 5) && at(b + 6);
          if (!core) continue;
          const before = !at(b - 1) && !at(b - 2) && !at(b - 3) && !at(b - 4);
          const after = !at(b + 7) && !at(b + 8) && !at(b + 9) && !at(b + 10);
          if (before || after) score += 40;
        }
      }
    };
    lines((y, x) => this.m[y]![x]!);
    lines((x, y) => this.m[y]![x]!);
    for (let y = 0; y < s - 1; y += 1) {
      for (let x = 0; x < s - 1; x += 1) {
        const c = this.m[y]![x];
        if (c === this.m[y]![x + 1] && c === this.m[y + 1]![x] && c === this.m[y + 1]![x + 1]) score += 3;
      }
    }
    let dark = 0;
    for (const row of this.m) for (const v of row) if (v) dark += 1;
    const total = s * s;
    score += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
    return score;
  }
}

/** The module grid for `text`, dark = true, without the quiet zone. */
export function qrMatrix(text: string): Grid {
  const { ver, codewords } = dataWithEcc(text);
  let best: Grid | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask += 1) {
    const q = new Matrix(ver);
    q.drawFunctionPatterns();
    q.placeCodewords(codewords);
    q.applyMask(mask);
    q.formatBits(mask);
    const score = q.penalty();
    if (score < bestScore) { bestScore = score; best = q.m.map((r) => r.slice()); }
  }
  return best!;
}

/** An SVG path covering the dark modules, offset by a 4-module quiet zone. */
export function qrSvgPath(grid: Grid): { path: string; size: number } {
  const quiet = 4;
  let path = '';
  grid.forEach((row, y) => row.forEach((dark, x) => {
    if (dark) path += `M${x + quiet} ${y + quiet}h1v1h-1z`;
  }));
  return { path, size: grid.length + quiet * 2 };
}
